import React, {lazy, Suspense} from 'react';
import {Navigate, RouteObject} from 'react-router-dom';
import {Spin} from 'antd';
import MainLayout from '../components/layout/MainLayout';
import AuthGuard from './AuthGuard';
import {getDefaultLandingPath} from '@/utils/defaultLandingPath';
import {hasPermission} from '@/utils/auth';

const Login = lazy(() => import('../pages/auth/Login'));
const FaceVerificationPage = lazy(() => import('../pages/face/FaceVerificationPage'));
const Dashboard = lazy(() => import('../pages/dashboard/Dashboard'));
const MerchantStatistics = lazy(() => import('../pages/dashboard/MerchantStatistics'));
const UserList = lazy(() => import('../pages/user/UserList'));
const RoleList = lazy(() => import('../pages/role/RoleList'));
const PermissionList = lazy(() => import('../pages/permission/PermissionList'));
const PlatformProductList = lazy(() => import('../pages/product/PlatformProductList'));
const JdPlatformProductList = lazy(() => import('../pages/product/JdPlatformProductList'));
const JdMerchantProductList = lazy(() => import('../pages/product/JdMerchantProductList'));
const MerchantProductList = lazy(() => import('../pages/product/MerchantProductList'));
const LongRentPlatformProductList = lazy(() => import('../pages/product/LongRentPlatformProductList'));
const LongRentMerchantProductList = lazy(() => import('../pages/product/LongRentMerchantProductList'));
const OfflineProductList = lazy(() => import('../pages/product/OfflineProductList'));
const OfflineProductEdit = lazy(() => import('../pages/product/OfflineProductEdit'));
const ProductEdit = lazy(() => import('../pages/product/ProductEdit'));
const ProductImageUpload = lazy(() => import('../pages/product/ProductImageUpload'));
const CategoryList = lazy(() => import('../pages/product/CategoryList'));
const BrandList = lazy(() => import('../pages/product/BrandList'));
const TagList = lazy(() => import('../pages/product/TagList'));
const ProductModelList = lazy(() => import('../pages/product/ProductModelList'));
const ModelEnrollmentList = lazy(() => import('../pages/product/ModelEnrollmentList'));
const ModelEnrollmentAdminList = lazy(() => import('../pages/product/ModelEnrollmentAdminList'));
const SpecificationList = lazy(() => import('../pages/product/SpecificationList'));
const Order = lazy(() => import('../pages/order/Order'));
const OrderDetail = lazy(() => import('../pages/order/OrderDetail'));
const OrderStatistics = lazy(() => import('../pages/order/OrderStatistics'));
const AfterSaleList = lazy(() => import('../pages/order/AfterSaleList'));
const ComplaintList = lazy(() => import('../pages/order/ComplaintList'));
const MeituanOrder = lazy(() => import('../pages/meituanOrder/MeituanOrder'));
const MeituanOrderDetail = lazy(() => import('../pages/meituanOrder/MeituanOrderDetail'));
const MeituanOrderStatistics = lazy(() => import('../pages/meituanOrder/MeituanOrderStatistics'));
const JdOrder = lazy(() => import('../pages/jdOrder/JdOrder'));
const JdOrderDetail = lazy(() => import('../pages/jdOrder/JdOrderDetail'));
const JdOrderStatistics = lazy(() => import('../pages/jdOrder/JdOrderStatistics'));
const OfflineOrderList = lazy(() => import('../pages/offlineOrder/OfflineOrderList'));
const OfflineOrderDetail = lazy(() => import('../pages/offlineOrder/OfflineOrderDetail'));
const BuyoutOrderList = lazy(() => import('../pages/order/BuyoutOrderList'));
const MeituanProductList = lazy(() => import('../pages/meituanProduct/MeituanProductList'));
const MeituanProductEdit = lazy(() => import('../pages/meituanProduct/MeituanProductEdit'));
const MeituanProductUpload = lazy(() => import('../pages/meituanProduct/MeituanProductUpload'));
const SystemSettings = lazy(() => import('../pages/system/SystemSettings'));
const HomeModuleList = lazy(() => import('../pages/page/HomeModuleList'));
const HomeModuleEdit = lazy(() => import('../pages/page/HomeModuleEdit'));
const RecycleModuleList = lazy(() => import('../pages/page/RecycleModuleList'));
const RecycleModuleEdit = lazy(() => import('../pages/page/RecycleModuleEdit'));
const MemberList = lazy(() => import('../pages/member/MemberList'));
const MemberDetail = lazy(() => import('../pages/member/MemberDetail'));
const MerchantList = lazy(() => import('../pages/merchant/MerchantList'));
const MerchantEdit = lazy(() => import('../pages/merchant/MerchantEdit'));
const AlipaySubjectList = lazy(() => import('../pages/alipaySubject/AlipaySubjectList'));
const AlipaySubjectEdit = lazy(() => import('../pages/alipaySubject/AlipaySubjectEdit'));
const MerchantLeadList = lazy(() => import('../pages/merchantLead/MerchantLeadList'));
const MerchantLeadEdit = lazy(() => import('../pages/merchantLead/MerchantLeadEdit'));
const FinanceSettlementList = lazy(() => import('../pages/finance/SettlementList'));
const FinanceOfflineSettlementList = lazy(() => import('../pages/finance/OfflineSettlementList'));
const FinanceRevenueManagement = lazy(() => import('../pages/finance/RevenueManagement'));
const FinanceDepositManagement = lazy(() => import('../pages/finance/DepositManagement'));
const VoucherActivityList = lazy(() => import('../pages/voucher/VoucherActivityList'));
const CreateVoucherActivity = lazy(() => import('../pages/voucher/CreateVoucherActivity'));
const ModifyVoucherActivity = lazy(() => import('../pages/voucher/ModifyVoucherActivity'));
const MarketingImageList = lazy(() => import('../pages/voucher/MarketingImageList'));

// 回收功能相关页面
const RecycleCategoryList = lazy(() => import('../pages/recycle/category/RecycleCategoryList'));
const RecycleCategoryEdit = lazy(() => import('../pages/recycle/category/RecycleCategoryEdit'));
const RecycleOrderList = lazy(() => import('../pages/recycle/order/RecycleOrderList'));
const RecycleOrderDetail = lazy(() => import('../pages/recycle/order/RecycleOrderDetail'));
const RecycleValuationList = lazy(() => import('../pages/recycle/valuation/RecycleValuationList'));
const RecycleValuationEdit = lazy(() => import('../pages/recycle/valuation/RecycleValuationEdit'));
const RecycleQualificationEdit = lazy(() => import('../pages/recycle/qualification/RecycleQualificationEdit'));
const RecycleQualificationDetail = lazy(() => import('../pages/recycle/qualification/RecycleQualificationDetail'));

//points积分相关页面
const PointsRuleConfig = lazy(() => import('../pages/points/PointsRuleConfigList'));
const PointsProduct = lazy(() => import('../pages/points/PointsProductList'));
const PointsOrder = lazy(() => import('../pages/points/PointsOrderList'));

//tweet 推文相关页面
const TweetCategoryList = lazy(() => import('../pages/tweet/TweetCategoryList'));
const TweetTopicList = lazy(() => import('../pages/tweet/TweetTopicList'));
const TweetList = lazy(() => import('../pages/tweet/TweetList'));
const TweetCommentList = lazy(() => import('../pages/tweet/TweetCommentList'));

//门店相关页面
const StoreList = lazy(() => import('../pages/store/StoreList'));

//风控管理相关页面
const RiskControlReportList = lazy(() => import('../pages/riskControl/RiskControlReportList'));
const SmsSendRecordList = lazy(() => import('../pages/sms/SmsSendRecordList'));

const LazyComponent: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<Spin size="large" className="global-spin" />}>{children}</Suspense>
);

const RoleAwareHomeRedirect: React.FC = () => (
  <Navigate to={getDefaultLandingPath()} replace />
);

const ProductListRedirect: React.FC = () => {
  const target = hasPermission('PLATFORM_PRODUCT_MANAGE') ? '/product/platform/list' : '/product/merchant/list';
  return <Navigate to={target} replace />;
};

const routes: RouteObject[] = [
  {
    path: '/login',
    element: <LazyComponent><Login /></LazyComponent>,
  },
  {
    path: '/face',
    element: (
      <Suspense
        fallback={
          <div
            style={{
              display: 'flex',
              minHeight: '100vh',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <Spin size="large" style={{ marginBottom: 12 }} />
            <div style={{ color: '#666', fontSize: 14 }}>正在准备人脸认证，请耐心等待...</div>
          </div>
        }
      >
        <FaceVerificationPage />
      </Suspense>
    ),
  },
  {
    path: '/',
    element: (
      <AuthGuard>
        <MainLayout />
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: <RoleAwareHomeRedirect />,
      },
      {
        path: '/dashboard',
        children: [
          {
            path: '/dashboard',
            element: <Navigate to="/dashboard/operations" replace />,
          },
          {
            path: '/dashboard/operations',
            element: <LazyComponent><Dashboard /></LazyComponent>,
          },
          {
            path: '/dashboard/merchant-statistics',
            element: <LazyComponent><MerchantStatistics /></LazyComponent>,
          },
        ],
      },
      {
        path: '/user',
        element: <LazyComponent><UserList /></LazyComponent>,
      },
      {
        path: '/role',
        element: <LazyComponent><RoleList /></LazyComponent>,
      },
      {
        path: '/permission',
        element: <LazyComponent><PermissionList /></LazyComponent>,
      },
      {
        path: '/page',
        children: [
          {
            path: '/page/home',
            element: <LazyComponent><HomeModuleList /></LazyComponent>,
          },
          {
            path: '/page/home-module/edit',
            element: <LazyComponent><HomeModuleEdit /></LazyComponent>,
          },
          {
            path: '/page/home-module/edit/:id',
            element: <LazyComponent><HomeModuleEdit /></LazyComponent>,
          },
          {
            path: '/page/recycle',
            element: <LazyComponent><RecycleModuleList /></LazyComponent>,
          },
          {
            path: '/page/recycle-module/edit',
            element: <LazyComponent><RecycleModuleEdit /></LazyComponent>,
          },
          {
            path: '/page/recycle-module/edit/:id',
            element: <LazyComponent><RecycleModuleEdit /></LazyComponent>,
          },
        ],
      },
      {
        path: '/product',
        children: [
          {
            path: '/product/list',
            element: <LazyComponent><ProductListRedirect /></LazyComponent>,
          },
          {
            path: '/product/platform/list',
            element: <LazyComponent><PlatformProductList /></LazyComponent>,
          },
          {
            path: '/product/jd/list',
            element: <LazyComponent><JdPlatformProductList /></LazyComponent>,
          },
          {
            path: '/product/jd/merchant/list',
            element: <LazyComponent><JdMerchantProductList /></LazyComponent>,
          },
          {
            path: '/product/merchant/list',
            element: <LazyComponent><MerchantProductList /></LazyComponent>,
          },
          {
            path: '/product/long-rent-platform/list',
            element: <LazyComponent><LongRentPlatformProductList /></LazyComponent>,
          },
          {
            path: '/product/long-rent-platform/new',
            element: <LazyComponent><ProductEdit /></LazyComponent>,
          },
          {
            path: '/product/long-rent-platform/:id/edit',
            element: <LazyComponent><ProductEdit /></LazyComponent>,
          },
          {
            path: '/product/long-rent-merchant/list',
            element: <LazyComponent><LongRentMerchantProductList /></LazyComponent>,
          },
          {
            path: '/product/long-rent-merchant/new',
            element: <LazyComponent><ProductEdit /></LazyComponent>,
          },
          {
            path: '/product/long-rent-merchant/:id/edit',
            element: <LazyComponent><ProductEdit /></LazyComponent>,
          },
          {
            path: '/product/offline/list',
            element: <LazyComponent><OfflineProductList /></LazyComponent>,
          },
          {
            path: '/product/offline/new',
            element: <LazyComponent><OfflineProductEdit /></LazyComponent>,
          },
          {
            path: '/product/offline/:id/edit',
            element: <LazyComponent><OfflineProductEdit /></LazyComponent>,
          },
          {
            path: '/product/new',
            element: <LazyComponent><ProductEdit /></LazyComponent>,
          },
          {
            path: '/product/:id/edit',
            element: <LazyComponent><ProductEdit /></LazyComponent>,
          },
          {
            path: '/product/category',
            element: <LazyComponent><CategoryList /></LazyComponent>,
          },
          {
            path: '/product/brand',
            element: <LazyComponent><BrandList /></LazyComponent>,
          },
          {
            path: '/product/tag',
            element: <LazyComponent><TagList /></LazyComponent>,
          },
          {
            path: '/product/productModel',
            element: <LazyComponent><ProductModelList /></LazyComponent>,
          },
          {
            path: '/product/model-enrollment',
            element: <LazyComponent><ModelEnrollmentList /></LazyComponent>,
          },
          {
            path: '/product/model-enrollment-admin',
            element: <LazyComponent><ModelEnrollmentAdminList /></LazyComponent>,
          },
          {
            path: '/product/specification',
            element: <LazyComponent><SpecificationList /></LazyComponent>,
          },
          {
            path: '/product/image/upload',
            element: <LazyComponent><ProductImageUpload /></LazyComponent>,
          },
        ],
      },
      {
        path: '/alipay-product',
        element: <LazyComponent><ProductListRedirect /></LazyComponent>,
      },
      {
        path: '/jd-product',
        element: <Navigate to="/product/jd/list" replace />,
      },
      {
        path: '/meituan-product',
        children: [
          {
            path: '/meituan-product/list',
            element: <LazyComponent><MeituanProductList /></LazyComponent>,
          },
          {
            path: '/meituan-product/upload',
            element: <LazyComponent><MeituanProductUpload /></LazyComponent>,
          },
          {
            path: '/meituan-product/new',
            element: <LazyComponent><MeituanProductEdit /></LazyComponent>,
          },
          {
            path: '/meituan-product/:id/edit',
            element: <LazyComponent><MeituanProductEdit /></LazyComponent>,
          },
        ],
      },
      {
        path: '/order',
        children: [
          {
            path: '/order/list',
            element: <LazyComponent><Order /></LazyComponent>,
          },
          {
            path: '/order/list/:id',
            element: <LazyComponent><OrderDetail /></LazyComponent>,
          },
          {
            path: '/order/long-rent/list',
            element: <LazyComponent><Order /></LazyComponent>,
          },
          {
            path: '/order/long-rent/list/:id',
            element: <LazyComponent><OrderDetail /></LazyComponent>,
          },
          {
            path: '/order/statistics',
            element: <LazyComponent><OrderStatistics /></LazyComponent>,
          },
          {
            path: '/order/aftersalelist',
            element: <LazyComponent><AfterSaleList /></LazyComponent>,
          },
          {
            path: '/order/complaint',
            element: <LazyComponent><ComplaintList /></LazyComponent>,
          },
          {
            path: '/order/meituan',
            element: <LazyComponent><MeituanOrder /></LazyComponent>,
          },
          {
            path: '/order/meituan/:id',
            element: <LazyComponent><MeituanOrderDetail /></LazyComponent>,
          },
          {
            path: '/order/meituan-statistics',
            element: <LazyComponent><MeituanOrderStatistics /></LazyComponent>,
          },
          {
            path: '/order/jd',
            element: <LazyComponent><JdOrder /></LazyComponent>,
          },
          {
            path: '/order/jd/:id',
            element: <LazyComponent><JdOrderDetail /></LazyComponent>,
          },
          {
            path: '/order/jd-statistics',
            element: <LazyComponent><JdOrderStatistics /></LazyComponent>,
          },
          {
            path: '/order/offline',
            element: <LazyComponent><OfflineOrderList /></LazyComponent>,
          },
          {
            path: '/order/offline/:id',
            element: <LazyComponent><OfflineOrderDetail /></LazyComponent>,
          },
          {
            path: '/order/buyout',
            element: <LazyComponent><BuyoutOrderList /></LazyComponent>,
          },
        ],
      },
      {
        path: '/risk-control',
        children: [
          {
            path: '/risk-control/reports',
            element: <LazyComponent><RiskControlReportList /></LazyComponent>,
          },
          {
            path: '/risk-control/sms-send-records',
            element: <LazyComponent><SmsSendRecordList /></LazyComponent>,
          },
        ],
      },
      {
        path: '/points',
        children: [
          {
            path: '/points/ruleConfig',
            element: <LazyComponent><PointsRuleConfig /></LazyComponent>,
          },
          {
            path: '/points/pointsProduct',
            element: <LazyComponent><PointsProduct /></LazyComponent>,
          },
          {
            path: '/points/pointsOrder',
            element: <LazyComponent><PointsOrder /></LazyComponent>,
          },
        ],
      },
      {
        path: '/finance',
        children: [
          {
            path: '/finance/settlement',
            element: <LazyComponent><FinanceSettlementList /></LazyComponent>,
          },
          {
            path: '/finance/offline-settlement',
            element: <LazyComponent><FinanceOfflineSettlementList /></LazyComponent>,
          },
          {
            path: '/finance/revenue',
            element: <LazyComponent><FinanceRevenueManagement /></LazyComponent>,
          },
          {
            path: '/finance/deposit',
            element: <LazyComponent><FinanceDepositManagement /></LazyComponent>,
          },
        ],
      },
      {
        path: '/voucher',
        children: [
          {
            path: '/voucher/activity/list',
            element: <LazyComponent><VoucherActivityList /></LazyComponent>,
          },
          {
            path: '/voucher/activity/create',
            element: <LazyComponent><CreateVoucherActivity /></LazyComponent>,
          },
          {
            path: '/voucher/activity/modify',
            element: <LazyComponent><ModifyVoucherActivity /></LazyComponent>,
          },
          {
            path: '/voucher/marketing/image/list',
            element: <LazyComponent><MarketingImageList /></LazyComponent>,
          },
        ],
      },
      {
        path: '/system',
        element: <LazyComponent><SystemSettings /></LazyComponent>,
      },
      {
        path: '/member',
        element: <LazyComponent><MemberList /></LazyComponent>,
      },
      {
        path: '/member/detail/:id',
        element: <LazyComponent><MemberDetail /></LazyComponent>,
      },
      {
        path: '/merchant',
        children: [
          {
            path: '/merchant',
            element: <LazyComponent><MerchantList /></LazyComponent>,
          },
          {
            path: '/merchant/edit',
            element: <LazyComponent><MerchantEdit /></LazyComponent>,
          },
          {
            path: '/merchant/edit/:id',
            element: <LazyComponent><MerchantEdit /></LazyComponent>,
          },
        ],
      },
      {
        path: '/alipay-subject',
        children: [
          {
            path: '/alipay-subject',
            element: <LazyComponent><AlipaySubjectList /></LazyComponent>,
          },
          {
            path: '/alipay-subject/edit',
            element: <LazyComponent><AlipaySubjectEdit /></LazyComponent>,
          },
          {
            path: '/alipay-subject/edit/:id',
            element: <LazyComponent><AlipaySubjectEdit /></LazyComponent>,
          },
        ],
      },
      {
        path: '/merchant-lead',
        children: [
          {
            path: '/merchant-lead',
            element: <LazyComponent><MerchantLeadList /></LazyComponent>,
          },
          {
            path: '/merchant-lead/edit',
            element: <LazyComponent><MerchantLeadEdit /></LazyComponent>,
          },
          {
            path: '/merchant-lead/edit/:id',
            element: <LazyComponent><MerchantLeadEdit /></LazyComponent>,
          },
        ],
      },
      {
        path: '/store',
        children: [
          {
            path: '/store',
            element: <LazyComponent><StoreList /></LazyComponent>,
          },
        ],
      },
      {
        path: '/recycle',
        children: [
          {
            path: '/recycle/categories',
            element: <LazyComponent><RecycleCategoryList /></LazyComponent>,
          },
          {
            path: '/recycle/categories/create',
            element: <LazyComponent><RecycleCategoryEdit /></LazyComponent>,
          },
          {
            path: '/recycle/categories/:id/edit',
            element: <LazyComponent><RecycleCategoryEdit /></LazyComponent>,
          },
          {
            path: '/recycle/orders',
            element: <LazyComponent><RecycleOrderList /></LazyComponent>,
          },
          {
            path: '/recycle/orders/:orderId',
            element: <LazyComponent><RecycleOrderDetail /></LazyComponent>,
          },
          {
            path: '/recycle/valuations',
            element: <LazyComponent><RecycleValuationList /></LazyComponent>,
          },
          {
            path: '/recycle/valuations/create',
            element: <LazyComponent><RecycleValuationEdit /></LazyComponent>,
          },
          {
            path: '/recycle/valuations/:id/edit',
            element: <LazyComponent><RecycleValuationEdit /></LazyComponent>,
          },
          {
            path: '/recycle/qualifications/create/:orderId',
            element: <LazyComponent><RecycleQualificationEdit /></LazyComponent>,
          },
          {
            path: '/recycle/qualifications/edit/:orderId/:id',
            element: <LazyComponent><RecycleQualificationEdit /></LazyComponent>,
          },
          {
            path: '/recycle/qualifications/detail/:id',
            element: <LazyComponent><RecycleQualificationDetail /></LazyComponent>,
          },
        ],
      },
      {
        path: '/tweet',
        children: [
          {
            path: '/tweet/tweets',
            element: <LazyComponent><TweetList /></LazyComponent>,
          },
          {
            path: '/tweet/category',
            element: <LazyComponent><TweetCategoryList /></LazyComponent>,
          },
          {
            path: '/tweet/topic',
            element: <LazyComponent><TweetTopicList /></LazyComponent>,
          },
          {
            path: '/tweet/comments',
            element: <LazyComponent><TweetCommentList /></LazyComponent>,
          },
        ],
      },
    ],
  },
];

export default routes;
